import axios from "axios";
import ErrorHandler from "../../shared/utils/errorHandler";

export const REQUEST_TIMEOUT_MS = 60000;

const RETRY_DELAY_MS = 800;
const RETRYABLE_STATUSES = [502, 503, 504];

const isRetryable = (error) => {
  const config = error?.config;
  if (!config || (config.method || "get").toLowerCase() !== "get") return false;
  if (config._retried || config.noRetry) return false;
  if (axios.isCancel(error) || error?.isRateLimited) return false;
  if (!error.response) return ErrorHandler.isNetworkError(error);
  return RETRYABLE_STATUSES.includes(error.response.status);
};

// Retries an idempotent GET once on network/timeout/gateway failures (cold backend, flaky mobile data).
export const retryOnce = async (instance, error) => {
  if (!isRetryable(error)) return null;
  error.config._retried = true;
  await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
  return instance.request(error.config);
};

// Guarantees error.response.data.message always holds a specific, human-readable reason,
// so every screen's `err?.response?.data?.message || 'fallback'` shows the real cause.
export const normalizeApiError = (error) => {
  if (!error || axios.isCancel(error) || error.isRateLimited || error._normalized) return error;
  error._normalized = true;

  const { message } = ErrorHandler.getMessage(error);
  const data = error.response?.data;
  const serverMessage = data && typeof data === "object" ? data.message : null;

  if (!serverMessage) {
    if (!error.response) {
      error.response = { status: 0, data: { message }, synthetic: true };
    } else if (data && typeof data === "object") {
      data.message = message;
    } else {
      error.response.data = { message };
    }
  }
  error.userMessage = serverMessage || message;
  return error;
};

// Only an expired/invalid session (401) should log the user out; 403 means "no permission".
export const isSessionError = (error) =>
  error?.response?.status === 401 ||
  error?.response?.data?.errorCode === "USER_NOT_FOUND";
